/**
 * deductionApi.ts
 * 공통 일차감 API 클라이언트
 *
 * @description
 * - 협력사/라이더 일차감에서 공통으로 사용하는 API 함수
 * - 엔드포인트: /api/v1/deduct
 */
import { apiClient } from "@/shared/api/client";
import { ApiResponseDto, ToggleUseYnRequestDto, DeleteRequestDto } from "@/shared/types/apiTypes";

// ============================================================
// 공통 타입 정의
// ============================================================

/**
 * 일차감 DTO 기본 구조
 */
export interface DeductionDto {
    seqNo: number;
    deductTarget: string;       // S: 협력사, SA: 전체협력사, R: 라이더
    deductRegType: string;      // C: 본사 등록, S: 협력사 등록
    suppId: string;
    riderId: string;
    contStatus?: string | null;
    contEdDate?: string | null;
    deductType: string;         // WORK_DAY, DAILY, RENT, LEASE, ETC
    calcType: string;           // 1: 정액, 2: 정률
    deductName: string;
    deductCnt: number | null;
    deductAmt: number | null;
    deductRate: number | null;
    deductDispYn: string;
    startDay: string;
    endDay: string;
    insDate: string;
    insUser: string;
    updDate: string | null;
    updUser: string | null;
    useYn?: string;
}

/**
 * 일차감 목록 조회 응답 DTO
 */
export interface GetDeductionListResponseDto {
    totalCount: number;
    deducts: DeductionDto[];
}

/**
 * 일차감 등록/수정 응답 DTO
 */
export interface RegisterDeductionResponseDto {
    success: boolean;
    message?: string;
    seqNo?: number;
}

/**
 * 일차감 등록 요청 DTO 기본 구조
 */
export interface RegisterDeductionRequestDto {
    deductTarget: string;
    deductRegType?: string;
    suppId: string;
    riderId?: string | null;
    deductType: string;
    calcType: string;
    deductName: string;
    deductCnt?: number | null;
    deductAmt?: number | null;
    deductRate?: number | null;
    deductDispYn: string;
    startDay: string;
    endDay: string;
    insUser: string;
}

/**
 * 일차감 수정 요청 DTO 기본 구조
 */
export interface UpdateDeductionRequestDto {
    deductTarget: string;
    suppId: string;
    riderId?: string | null;
    deductType: string;
    calcType: string;
    deductName: string;
    deductCnt?: number | null;
    deductAmt?: number | null;
    deductRate?: number | null;
    deductDispYn: string;
    startDay: string;
    endDay: string;
    updUser: string;
}

/**
 * 일차감 목록 조회 필터
 */
export interface DeductionListFilter {
    deductTarget?: string;
    deductRegType?: string; // 등록 유형 (C: 본사, S: 협력사)
    suppId?: string;
    riderId?: string;
    startDate?: string;
    endDate?: string;
}

// ============================================================
// API 경로 상수
// ============================================================

const DEDUCT_BASE_PATH = '/api/v1/deduct';
const RIDER_BASE_PATH = '/api/v1/rider';

// ============================================================
// 공통 일차감 API
// ============================================================

/**
 * 공통 일차감 API 서비스
 * 협력사/라이더 일차감에서 공통으로 사용하는 API 함수들
 */
export const deductionApi = {
    /**
     * 일차감 목록 조회
     * GET /api/v1/deduct/list
     * @param filter 검색 필터 (deductTarget, suppId, riderId, startDate, endDate)
     */
    getDeductionList: async (filter?: DeductionListFilter): Promise<GetDeductionListResponseDto> => {
        const params: Record<string, string> = {};

        if (filter?.deductTarget) params.deductTarget = filter.deductTarget;
        if (filter?.deductRegType) params.deductRegType = filter.deductRegType;
        if (filter?.suppId) params.suppId = filter.suppId;
        if (filter?.riderId) params.riderId = filter.riderId;
        if (filter?.startDate) params.startDate = filter.startDate;
        if (filter?.endDate) params.endDate = filter.endDate;

        const response = await apiClient.get<GetDeductionListResponseDto>(
            `${DEDUCT_BASE_PATH}/list`,
            { params }
        );
        return response.data;
    },

    /**
     * 일차감 등록
     * POST /api/v1/deduct/register
     */
    registerDeduction: async (request: RegisterDeductionRequestDto): Promise<RegisterDeductionResponseDto> => {
        const response = await apiClient.post<RegisterDeductionResponseDto>(
            `${DEDUCT_BASE_PATH}/register`,
            request
        );
        return response.data;
    },

    /**
     * 일차감 수정
     * PUT /api/v1/deduct/{seqNo}
     */
    updateDeduction: async (seqNo: number, request: UpdateDeductionRequestDto): Promise<ApiResponseDto> => {
        const response = await apiClient.put<ApiResponseDto>(
            `${DEDUCT_BASE_PATH}/${seqNo}`,
            request
        );
        return response.data;
    },

    /**
     * 일차감 삭제 (다중 삭제 지원)
     * DELETE /api/v1/deduct
     */
    deleteDeductions: async (request: DeleteRequestDto): Promise<ApiResponseDto> => {
        const response = await apiClient.delete<ApiResponseDto>(
            DEDUCT_BASE_PATH,
            { data: request }
        );
        return response.data;
    },

    /**
     * 일차감 활성/비활성 토글
     * PATCH /api/v1/deduct/toggle-use
     */
    toggleUseYn: async (request: ToggleUseYnRequestDto): Promise<ApiResponseDto> => {
        const response = await apiClient.patch<ApiResponseDto>(
            `${DEDUCT_BASE_PATH}/toggle-use`,
            request
        );
        return response.data;
    }
};

// ============================================================
// 공통 라이더 API
// ============================================================

/**
 * 라이더 목록 응답 DTO
 */
export interface RiderInfoDto {
    riderId: string;
    riderName: string;
    suppId: string;
    statusCode?: string;
    contEdDate?: string;
}

/**
 * 라이더 목록 조회 응답 DTO
 */
export interface GetRiderListResponseDto {
    totalCount: number;
    riders: RiderInfoDto[];
}

/**
 * 공통 라이더 API 서비스
 * 여러 모듈에서 공통으로 사용하는 라이더 관련 API
 */
export const riderApi = {
    /**
     * 라이더 목록 조회 (협력사별)
     * GET /api/v1/rider/list
     * @param suppId 협력사 ID
     * @param statusCode 계약 상태 코드 (선택, 예: 'UNDER_CONTRACT')
     */
    getRiderList: async (suppId: string, statusCode?: string): Promise<GetRiderListResponseDto> => {
        const params: Record<string, string> = { suppId };
        if (statusCode) {
            params.statusCode = statusCode;
        }

        const response = await apiClient.get<GetRiderListResponseDto>(
            `${RIDER_BASE_PATH}/list`,
            { params }
        );
        return response.data;
    },

    /**
     * 계약 중인 라이더 목록 조회
     * GET /api/v1/rider/list?statusCode=UNDER_CONTRACT
     * @param suppId 협력사 ID
     */
    getActiveRiderList: async (suppId: string): Promise<GetRiderListResponseDto> => {
        return riderApi.getRiderList(suppId, 'UNDER_CONTRACT');
    }
};
